import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Image, Text, View } from '@react-pdf/renderer';

import FontAwesomeIcon from 'src/components/custom_components/PdfFontAwesomeIcon';

import { Disease, DiseaseFullName } from 'src/models/Disease.model';
import { Eye } from 'src/models/Eye.model';
import {
  Grading,
  GradingIsPositive,
  GradingName,
} from 'src/models/Grading.model';
import { handleNSI18Next } from 'src/models/Language.model';
import { Result } from 'src/models/Result.model';
import { Session } from 'src/models/Session.model';

import { useHistoryResult } from 'src/hooks/useHistoryResult';

import { cls } from 'src/utils/reactPdfStyles';

interface IHeatmapSectionProps {
  session: Session;
  disease: Disease;
  historyIndex?: number;
}

const heatmapTr = (
  disease: Disease,
  getHistoryResult: (
    eye: Eye,
    disease: Disease,
    historyIndex?: number,
  ) => Result | undefined,
  historyIndex?: number,
) => {
  const left = getHistoryResult(Eye.LEFT, disease, historyIndex);
  const right = getHistoryResult(Eye.RIGHT, disease, historyIndex);
  const leftIsPositive = left !== undefined && GradingIsPositive(left.grading);
  const rightIsPositive =
    right !== undefined && GradingIsPositive(right.grading);

  if (leftIsPositive || rightIsPositive) {
    const leftHeatmapUrl = left?.heatmapFile || 'src/assets/img/noimage.gif';
    const rightHeatmapUrl = right?.heatmapFile || 'src/assets/img/noimage.gif';

    const { handleReportT } = handleNSI18Next();

    return (
      <View style={cls('d-flex hstack justify-content-between')}>
        <View style={cls('td w-50')}>
          {rightIsPositive ? (
            <Image src={rightHeatmapUrl} style={cls('rounded')} />
          ) : (
            <View style={cls('text-center align-middle')}>
              <Text style={cls('p text-muted')}>
                {handleReportT('heatmapSection.notAvailable')}
              </Text>
            </View>
          )}
        </View>

        <View style={cls('px-3 p-0 border-0')}></View>

        <View style={cls('td w-50')}>
          {leftIsPositive ? (
            <Image src={leftHeatmapUrl} style={cls('rounded')} />
          ) : (
            <View style={cls('text-center align-middle')}>
              <Text style={cls('p text-muted')}>
                {handleReportT('heatmapSection.notAvailable')}
              </Text>
            </View>
          )}
        </View>
      </View>
    );
  } else {
    return <></>;
  }
};

const diseaseTr = (
  disease: Disease,
  getHistoryResult: (
    eye: Eye,
    disease: Disease,
    historyIndex?: number,
  ) => Result | undefined,
  historyIndex?: number,
) => {
  const left = getHistoryResult(Eye.LEFT, disease, historyIndex);
  const right = getHistoryResult(Eye.RIGHT, disease, historyIndex);

  const { handleReportT } = handleNSI18Next();

  const leftIsPositive =
    left !== undefined &&
    GradingIsPositive(left.grading) &&
    left.heatmapFile !== undefined;
  const rightIsPositive =
    right !== undefined &&
    GradingIsPositive(right.grading) &&
    right.heatmapFile !== undefined;

  return leftIsPositive || rightIsPositive ? (
    <View style={cls('d-flex hstack justify-content-between')}>
      <View style={cls('td w-30 text-muted')}>
        {rightIsPositive && (
          <Text>{handleReportT(`resultSection.diseaseType`)}</Text>
        )}
      </View>
      <View style={cls('td w-20 text-muted')}>
        {rightIsPositive && (
          <Text>{handleReportT(`resultSection.status`)}</Text>
        )}
      </View>

      <View style={cls('px-3 p-0 border-0')}></View>

      <View style={cls('td w-30 text-muted')}>
        {leftIsPositive && (
          <Text>{handleReportT(`resultSection.diseaseType`)}</Text>
        )}
      </View>
      <View style={cls('td w-20 text-muted')}>
        {leftIsPositive && <Text>{handleReportT(`resultSection.status`)}</Text>}
      </View>
    </View>
  ) : (
    <></>
  );
};

const resultTr = (
  disease: Disease,
  getHistoryResult: (
    eye: Eye,
    disease: Disease,
    historyIndex?: number,
  ) => Result | undefined,
  historyIndex?: number,
) => {
  const left = getHistoryResult(Eye.LEFT, disease, historyIndex);
  const right = getHistoryResult(Eye.RIGHT, disease, historyIndex);

  // original results for comparison
  const originalLeft = getHistoryResult(Eye.LEFT, disease, 0);
  const originalRight = getHistoryResult(Eye.RIGHT, disease, 0);

  const { handleCommonT } = handleNSI18Next();

  const leftIsPositive =
    left !== undefined &&
    GradingIsPositive(left.grading) &&
    left.heatmapFile !== undefined;
  const rightIsPositive =
    right !== undefined &&
    GradingIsPositive(right.grading) &&
    right.heatmapFile !== undefined;

  if (leftIsPositive || rightIsPositive) {
    const leftIsSevere =
      left?.grading === Grading.HIGH_RISK ||
      left?.grading === Grading.PROLIFERATIVE_DR ||
      left?.grading === Grading.LATE_WET ||
      left?.grading === Grading.POSITIVE ||
      left?.grading === Grading.SEVERE_ATHEROSCLEROSIS;

    const rightIsSevere =
      right?.grading === Grading.HIGH_RISK ||
      right?.grading === Grading.PROLIFERATIVE_DR ||
      right?.grading === Grading.LATE_WET ||
      right?.grading === Grading.POSITIVE ||
      right?.grading === Grading.SEVERE_ATHEROSCLEROSIS;

    return (
      <View style={cls('d-flex hstack justify-content-between')}>
        <View style={cls('td w-30')}>
          {rightIsPositive && (
            <Text>
              {handleCommonT(`diseaseFullName.${DiseaseFullName(disease)}`)}:
            </Text>
          )}
        </View>
        <View style={cls('td w-20')}>
          {rightIsPositive && (
            <View
              style={cls(
                `me-1 badge rounded-pill ${rightIsSevere ? 'bg-danger' : 'bg-warning'}`,
              )}
            >
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                style={cls('me-2 text-white')}
              ></FontAwesomeIcon>
              <Text hyphenationCallback={(word) => [word]} style={cls('me-4')}>
                {handleCommonT(`gradingName.${GradingName(right.grading)}`)}
                {originalRight &&
                  right.grading.toString().toLowerCase() !==
                    originalRight.grading.toString().toLowerCase() &&
                  '*'}
              </Text>
            </View>
          )}
        </View>

        <View style={cls('px-3 p-0 border-0')}></View>

        <View style={cls('td w-30')}>
          {leftIsPositive && (
            <Text>
              {handleCommonT(`diseaseFullName.${DiseaseFullName(disease)}`)}:
            </Text>
          )}
        </View>
        <View style={cls('td w-20')}>
          {leftIsPositive && (
            <View
              style={cls(
                `me-1 badge rounded-pill ${leftIsSevere ? 'bg-danger' : 'bg-warning'}`,
              )}
            >
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                style={cls('me-2 text-white')}
              ></FontAwesomeIcon>

              <Text hyphenationCallback={(word) => [word]} style={cls('me-4')}>
                {handleCommonT(`gradingName.${GradingName(left.grading)}`)}
                {originalLeft &&
                  left.grading.toString().toLowerCase() !==
                    originalLeft.grading.toString().toLowerCase() &&
                  '*'}
              </Text>
            </View>
          )}
        </View>
      </View>
    );
  } else {
    return <></>;
  }
};

const HeatmapSection = ({
  session,
  disease,
  historyIndex,
}: IHeatmapSectionProps) => {
  const { handleReportT } = handleNSI18Next();
  const { getHistoryResult } = useHistoryResult(session);

  return (
    <View style={cls('mb-4')}>
      <View style={cls('flex')}>
        <Text style={cls('h2 report-header-no-logo')}>
          {handleReportT(`imageSection.heatmapImageTitle`)}
        </Text>
      </View>

      <Text style={cls('p mb-4')}>
        {handleReportT(`imageSection.heatmapDisclaimerParagraph`)}
      </Text>

      <View style={cls('d-flex hstack justify-content-between')}>
        <View style={cls('td w-50 border-0 b')}>
          <Text>{handleReportT(`imageSection.OD`)}</Text>
        </View>

        <View style={cls('px-3 p-0 border-0')}></View>

        <View style={cls('td w-50 border-0 b')}>
          <Text>{handleReportT(`imageSection.OS`)}</Text>
        </View>
      </View>

      {heatmapTr(disease, getHistoryResult, historyIndex)}
      {diseaseTr(disease, getHistoryResult, historyIndex)}
      {resultTr(disease, getHistoryResult, historyIndex)}
    </View>
  );
};

export default HeatmapSection;
